import {
    Dropdown,
    IDropdownOption,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    TextField,
} from "@fluentui/react";
import { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";

import { ICourseType } from "./DataTable";
import { loginRequest } from "../authConfig";
import {
    getDatasheetHeadlines,
    getDatasheetLayout,
    updateDatasheetLayout,
    updateCourseContent,
} from "../api";

interface ICourseTypeSettingsPanelProps {
    courseType: ICourseType;
    onDismiss: () => void;
    onSaved: (updatedCourseType: ICourseType) => void;
}

interface IDatasheetHeadline {
    dshid: number;
    dshName: string;
    dshCourseObjective: string;
    dshParticipants: string;
    dshMaterial: string;
    dshCourseContent: string;
    dshDefaultShow: boolean;
}

export default function CourseTypeSettingsPanel(
    props: ICourseTypeSettingsPanelProps
): JSX.Element {
    const { instance, accounts } = useMsal();

    const [token, setToken] = useState<string>("");
    const [headlines, setHeadlines] = useState<IDatasheetHeadline[]>([]);
    const [selectedDshId, setSelectedDshId] = useState<number | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [message, setMessage] = useState<{
        type: MessageBarType;
        text: string;
    } | null>(null);

    const [courseTitle, setCourseTitle] = useState<string>(
        props.courseType.kursusTitel
    );

    useEffect(() => {
        const loadData = async (): Promise<void> => {
            setIsLoading(true);
            setMessage(null);

            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0],
                });

                const accessToken = tokenResponse.accessToken;
                setToken(accessToken);

                const headlinesResponse =
                    await getDatasheetHeadlines(accessToken);

                setHeadlines(headlinesResponse.data);

                try {
                    const layoutResponse = await getDatasheetLayout(
                        accessToken,
                        props.courseType.kursustypeID
                    );

                    setSelectedDshId(layoutResponse.data.dshid);
                } catch (error) {
                    if (
                        error &&
                        error.response &&
                        error.response.status === 404
                    ) {
                        // Ingen DatasheetLayout endnu - brug fallback.
                        setSelectedDshId(1);
                    } else {
                        throw error;
                    }
                }
            } catch (error) {
                console.error(
                    "Fejl ved hentning af datasheet-indstillinger:",
                    error
                );

                setMessage({
                    type: MessageBarType.error,
                    text: "Overskriftsindstillingerne kunne ikke hentes.",
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [
        accounts,
        instance,
        props.courseType.kursustypeID,
    ]);

    const dropdownOptions: IDropdownOption[] = useMemo(
        () =>
            headlines.map((headline) => ({
                key: headline.dshid,
                text: headline.dshName,
            })),
        [headlines]
    );

    const selectedHeadline = useMemo(
        () =>
            headlines.find(
                (headline) => headline.dshid === selectedDshId
            ) ?? null,
        [headlines, selectedDshId]
    );

    const onHeadlineChange = (
        _event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ): void => {
        if (!option) {
            return;
        }

        setSelectedDshId(Number(option.key));
        setMessage(null);
    };

    const saveSettings = async (): Promise<void> => {
        if (selectedDshId === null) {
            setMessage({
                type: MessageBarType.warning,
                text: "Vælg en overskriftsskabelon.",
            });
            return;
        }

        if (!courseTitle.trim()) {
            setMessage({
                type: MessageBarType.warning,
                text: "Kursustitel må ikke være tom.",
            });
            return;
        }

        setIsSaving(true);
        setMessage(null);

        try {
            const updatedCourseType = {
                ...props.courseType,
                kursusTitel: courseTitle,
            };

            await updateCourseContent(
                token,
                updatedCourseType
            );

            await updateDatasheetLayout(
                token,
                props.courseType.kursustypeID,
                selectedDshId
            );

            props.onSaved(updatedCourseType);

            setMessage({
                type: MessageBarType.success,
                text: "Kursusindstillingerne er gemt.",
            });
        } catch (error) {
            console.error(
                "Fejl ved gemning af kursusindstillinger:",
                error
            );

            setMessage({
                type: MessageBarType.error,
                text: "Kursusindstillingerne kunne ikke gemmes.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Panel
            type={PanelType.medium}
            isOpen={true}
            onDismiss={props.onDismiss}
            closeButtonAriaLabel="Luk"
            headerText="Administrer overskrifter"
        >
            <h3>{props.courseType.kursusTitel}</h3>

            {isLoading ? (
                <Spinner
                    size={SpinnerSize.medium}
                    label="Indlæser overskrifter..."
                />
            ) : (
                <>
                    <Dropdown
                        label="Overskriftsskabelon"
                        placeholder="Vælg overskriftsskabelon"
                        options={dropdownOptions}
                        selectedKey={selectedDshId ?? undefined}
                        onChange={onHeadlineChange}
                        required
                    />

                    {selectedHeadline && (
                        <div style={{ marginTop: "24px" }}>
                            <Label>Kursusmål</Label>
                            <div style={{ marginBottom: "16px" }}>
                                {selectedHeadline.dshCourseObjective}
                            </div>

                            <Label>Deltagere</Label>
                            <div style={{ marginBottom: "16px" }}>
                                {selectedHeadline.dshParticipants}
                            </div>

                            <Label>Kursusmateriale</Label>
                            <div style={{ marginBottom: "16px" }}>
                                {selectedHeadline.dshMaterial}
                            </div>

                            <Label>Kursusindhold</Label>
                            <div style={{ marginBottom: "16px" }}>
                                {selectedHeadline.dshCourseContent}
                            </div>
                        </div>
                    )}

                    <TextField
                        label="Kursustitel"
                        value={courseTitle}
                        onChange={(_event, newValue) =>
                            setCourseTitle(newValue || "")
                        }
                        required
                        styles={{
                            root: {
                                marginTop: "24px",
                            },
                        }}
                    />

                    {message && (
                        <MessageBar
                            messageBarType={message.type}
                            onDismiss={() => setMessage(null)}
                            styles={{
                                root: {
                                    marginTop: "16px",
                                },
                            }}
                        >
                            {message.text}
                        </MessageBar>
                    )}

                    <PrimaryButton
                        text={isSaving ? "Gemmer..." : "Gem"}
                        onClick={saveSettings}
                        disabled={isSaving || selectedDshId === null}
                        styles={{
                            root: {
                                marginTop: "24px",
                            },
                        }}
                    />
                </>
            )}
        </Panel>
    );
}